/**
 * Affirmation — end-of-puzzle card.
 *
 * Looks like a small stamped index card placed on top of the notebook page.
 * Warm headline, specific stats (time, hints, mistakes — in honest plain
 * language), two clear CTAs.
 */
import { motion } from 'motion/react';

import { difficultyLabel, formatElapsed } from '@/games/sudoku/engine';
import type { SudokuDifficulty, SudokuMode } from '@/games/sudoku/engine';
import { isEmbedded } from '@/host/isEmbedded';

import { cardSpring } from './motion';

import './Affirmation.css';

export interface AffirmationProps {
  difficulty: SudokuDifficulty;
  mode: SudokuMode;
  elapsedSec: number;
  hintsUsed: number;
  mistakeCount: number;
  onAnother: () => void;
  onBack: () => void;
}

export function Affirmation({
  difficulty,
  mode,
  elapsedSec,
  hintsUsed,
  mistakeCount,
  onAnother,
  onBack,
}: AffirmationProps): React.JSX.Element {
  const headline = pickHeadline(difficulty, hintsUsed, mistakeCount);
  const sub = pickSub(elapsedSec, hintsUsed, mistakeCount);

  return (
    <motion.div
      className="nb-aff"
      role="region"
      aria-live="polite"
      initial={{ opacity: 0, y: 22, rotate: -1.8 }}
      animate={{ opacity: 1, y: 0, rotate: -1.2 }}
      transition={{ ...cardSpring, delay: 1.6 }}
    >
      <div className="nb-aff__tape nb-aff__tape--left" aria-hidden="true" />
      <div className="nb-aff__tape nb-aff__tape--right" aria-hidden="true" />

      <div className="nb-aff__card">
        <p className="nb-aff__eyebrow">
          <span className="nb-aff__eyebrow-rule" aria-hidden="true" />
          <span>
            {mode === 'daily' ? 'Daily' : 'Practice'} · {difficultyLabel(difficulty)}
          </span>
          <span className="nb-aff__eyebrow-rule" aria-hidden="true" />
        </p>

        <h2 className="nb-aff__headline">{headline}</h2>
        <p className="nb-aff__sub">{sub}</p>

        <dl className="nb-aff__stats">
          <div className="nb-aff__stat">
            <dt>Time</dt>
            <dd>{formatElapsed(elapsedSec)}</dd>
          </div>
          <div className="nb-aff__stat">
            <dt>Hints</dt>
            <dd>{hintsUsed === 0 ? 'None' : String(hintsUsed)}</dd>
          </div>
          <div className="nb-aff__stat">
            <dt>Mistakes</dt>
            <dd>{mistakeCount === 0 ? 'None' : String(mistakeCount)}</dd>
          </div>
          <div className="nb-aff__stat">
            <dt>Mode</dt>
            <dd>{mode === 'daily' ? 'Daily' : 'Practice'}</dd>
          </div>
        </dl>

        <div className="nb-aff__actions">
          {/* Embedded, `onBack` deliberately does not navigate (the host owns
              the exit), so the control would sit there doing nothing. */}
          {!isEmbedded() && (
            <button type="button" className="nb-aff__btn nb-aff__btn--secondary" onClick={onBack}>
              Back to puzzles
            </button>
          )}
          <button
            type="button"
            className="nb-aff__btn nb-aff__btn--primary"
            onClick={onAnother}
            autoFocus
          >
            {mode === 'daily' ? 'Practice another' : 'New puzzle'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function pickHeadline(difficulty: SudokuDifficulty, hints: number, mistakes: number): string {
  if (hints === 0 && mistakes === 0) {
    if (difficulty === 'hard' || difficulty === 'expert') return 'Solved with no help.';
    return 'Clean solve.';
  }
  if (mistakes === 0) return 'Solved.';
  if (hints === 0) return 'Solved — no hints needed.';
  return 'Solved.';
}

function pickSub(elapsedSec: number, hints: number, mistakes: number): string {
  if (elapsedSec < 60 * 5) return 'Faster than most. Well done.';
  if (elapsedSec < 60 * 12) return 'A good steady pace.';
  if (hints === 0 && mistakes === 0) return 'Patient and careful work.';
  if (mistakes > 4) return 'A few wrong turns, but you got there.';
  return 'Took your time. That works too.';
}
